<script setup lang="ts">
const { t } = useI18n();
const { toaster } = useToaster();
const { copyText } = useClipboard();
const { trackEvent } = useGAnalytics();
const appStateStore = useAppStateStore();
const merchant = appStateStore.currentMerchant;
const userStore = useUserStore();
const route = useRoute();
const isModalProfileBannerOpen = ref(false);
const headerBGImage = ref();
const headerIcon = ref();
const headerTitle = ref();

const copyToClipboard = async () => {
  const copied = await copyText(
    `${window.location.origin}/user/${userStore.id}`,
  );
  if (!copied) {
    toaster("error");
    return;
  }
  toaster("success", "copiedToClipboard");
};

const setHeroImage = () => {
  if (!route.fullPath.includes("user-portal")) {
    headerBGImage.value = getGameBanner(merchant);
    headerIcon.value = getGameIcon(merchant);
    headerTitle.value = t(`main.merchant.${merchant}`);
  } else {
    headerBGImage.value = getUserBanner(userStore.profileBanner);
  }
};

watch(
  () => userStore.profileBanner,
  (newVal) => {
    if (route.fullPath.includes("user-portal")) {
      headerBGImage.value = getUserBanner(newVal);
    }
  },
);

const isMobile = ref(false);

const { getMerchantAttributes } = useLuxuryDesign();
const heroAttributes = computed(() => getMerchantAttributes(unref(merchant)));

onMounted(() => {
  const checkMobile = () => {
    isMobile.value = window.innerWidth < 768;
  };
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

setHeroImage();
</script>
<template>
  <div
    v-if="
      !(
        !route.fullPath.includes('user-portal') &&
        merchant === 'diamond-dreams' &&
        isMobile
      )
    "
    v-bind="heroAttributes"
    class="relative w-full h-[186px] sm:h-[410px] bg-black/70 rounded-lg flex items-end justify-start hero-main"
    :class="['bg-[url(\'' + headerBGImage + '\')]']"
    :style="{
      backgroundImage: `url(${headerBGImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'top center',
      backgroundRepeat: 'no-repeat',
    }"
  >
    <div
      v-if="route.fullPath.includes('user-portal')"
      class="absolute top-4 right-4 p-1.5 sm:p-2 rounded-full cursor-pointer flex items-center justify-center transition-all duration-300 hero-main__edit-button"
      @click="
        ((isModalProfileBannerOpen = true), trackEvent('click_change_banner'))
      "
      data-test="profile-banner-edit-button"
    >
      <NuxtIcon
        name="mdi:wallpaper"
        class="w-6 sm:w-8 h-6 sm:h-8 hero-main__edit-icon !text-black"
      />
    </div>
    <div
      class="bg-neutral-800/70 px-3 sm:px-7 py-2 flex gap-1.5 items-center flex-1 rounded-b-lg flex-wrap"
    >
      <div class="w-14 sm:w-[72px] h-14 sm:h-[72px]">
        <CommonUserProfilePicture
          v-if="route.fullPath.includes('user-portal')"
          :profile-picture="userStore.profilePicture"
          size="custom"
          custom-class="w-full h-full"
        />
        <img
          v-else
          :src="headerIcon"
          :alt="$t(`main.merchant.${merchant}`)"
          class="w-full h-full rounded-lg"
        />
      </div>
      <div>
        <CommonUsername
          v-if="route.fullPath.includes('user-portal') && userStore.username"
          :username="{
            displayName: userStore.displayName,
            tagline: userStore.tagline,
            roles: userStore.roles,
          }"
          font-size="text-xl sm:text-2xl"
        />
        <h1
          v-else-if="
            route.fullPath.includes('user-portal') && !userStore.username
          "
          class="text-neutral-200 font-bold text-2xl"
        ></h1>
        <h4 v-else class="text-neutral-200 font-bold text-2xl font-nunito">
          {{ $t(`main.merchant.${merchant}`) }}
        </h4>
      </div>
      <div
        v-if="route.fullPath.includes('user-portal')"
        class="ml-auto flex items-center gap-2"
      >
        <BaseButtonSubmit
          size="custom"
          class="w-10 h-10 p-0 flex items-center justify-center sm:hidden"
          data-test="button-copy-profile-link-mobile"
          :contextual-styles="true"
          @click="copyToClipboard"
        >
          <template #text>
            <NuxtIcon
              name="mdi:share-variant"
              class="w-4 h-4 cursor-pointer hero-main__action-icon"
            />
          </template>
        </BaseButtonSubmit>
        <BaseButtonSubmit
          :text="$t('main.publicProfile.shareProfile')"
          class="hidden sm:flex w-36 lg:w-40"
          data-test="button-copy-profile-link-desktop"
          :contextual-styles="true"
          @click="copyToClipboard"
        >
        </BaseButtonSubmit>
      </div>
      <div
        v-if="
          !route.fullPath.includes('user-portal') &&
          merchant !== 'proof-of-luxe'
        "
        class="ml-auto flex items-center gap-4"
      >
        <NuxtLinkLocale
          v-if="appStateStore.getMerchantGamePageURI(merchant)"
          :to="appStateStore.getMerchantGamePageURI(merchant) || ''"
          target="_blank"
        >
          <BaseButtonSubmit
            size="custom"
            class="w-4 px-4 py-[7px] flex items-center justify-center lg:hidden"
            :contextual-styles="true"
          >
            <template #text>
              <NuxtIcon
                name="mdi:tray-download"
                class="w-4 h-4 hero-main__action-icon"
              />
            </template>
          </BaseButtonSubmit>
          <BaseButtonSubmit class="hidden lg:block" :contextual-styles="true">
            <template #text>
              {{ $t("main.headerComponent.downloadGame") }}
            </template>
            <template #image-suffix>
              <NuxtIcon
                name="mdi:tray-download"
                class="w-4 h-4 hero-main__action-icon"
              />
            </template>
          </BaseButtonSubmit>
        </NuxtLinkLocale>

        <NuxtLinkLocale
          v-if="appStateStore.getMerchantLandingPageURI(merchant)"
          :to="appStateStore.getMerchantLandingPageURI(merchant) || ''"
          target="_blank"
          class="hidden sm:block sm:ml-auto"
        >
          <BaseButtonSubmit
            size="custom"
            class="w-4 px-4 py-[7px] flex items-center justify-center lg:hidden"
            :contextual-styles="true"
          >
            <template #text>
              <NuxtIcon
                name="mdi:external-link"
                class="w-4 h-4 hero-main__action-icon"
              />
            </template>
          </BaseButtonSubmit>
          <BaseButtonSubmit class="hidden lg:block" :contextual-styles="true">
            <template #text>
              {{ $t("main.headerComponent.visitSite") }}
            </template>
            <template #image-suffix>
              <NuxtIcon
                name="mdi:external-link"
                class="w-4 h-4 hero-main__action-icon"
              />
            </template>
          </BaseButtonSubmit>
        </NuxtLinkLocale>
      </div>
    </div>
  </div>
  <div v-else class="-my-12"></div>
  <ModalUserBanner
    v-if="isModalProfileBannerOpen"
    @close="isModalProfileBannerOpen = false"
  />
</template>
